import { Link } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  getAiToolsConfig,
  getOpenAiModels,
  updateAiToolsConfig,
  AiToolFeatureRow,
} from '../api/aiToolsService';

function buildFormState(features: AiToolFeatureRow[]) {
  const out: Record<string, { model: string; prompt: string }> = {};
  for (const f of features) {
    out[f.id] = {
      model: f.model ?? '',
      prompt: f.prompt ?? '',
    };
  }
  return out;
}

const AiTools: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<string, { model: string; prompt: string }>>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const configQuery = useQuery({
    queryKey: ['aiToolsConfig'],
    queryFn: getAiToolsConfig,
  });

  const modelsQuery = useQuery({
    queryKey: ['openAiModels'],
    queryFn: getOpenAiModels,
    retry: false,
  });

  useEffect(() => {
    if (configQuery.data?.features) {
      setForm(buildFormState(configQuery.data.features));
    }
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, { model: string | null; prompt: string }> = {};
      for (const [id, row] of Object.entries(form)) {
        const m = row.model.trim();
        payload[id] = {
          model: m === '' ? null : m,
          prompt: row.prompt,
        };
      }
      return updateAiToolsConfig(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aiToolsConfig'] });
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    },
  });

  const modelOptions = useMemo(() => {
    const ids = modelsQuery.data?.map((m) => m.id) ?? [];
    const extra = new Set(ids);
    for (const row of Object.values(form)) {
      const m = row.model.trim();
      if (m && !extra.has(m)) {
        extra.add(m);
      }
    }
    return Array.from(extra).sort((a, b) => a.localeCompare(b));
  }, [modelsQuery.data, form]);

  const modelsUnavailable =
    modelsQuery.isError &&
    axios.isAxiosError(modelsQuery.error) &&
    modelsQuery.error.response?.status === 503;

  if (configQuery.isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </Layout>
    );
  }

  if (configQuery.isError) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-red-600">Failed to load AI tools configuration.</p>
        </div>
      </Layout>
    );
  }

  const features = configQuery.data?.features ?? [];

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex-auto">
            <h1 className="text-3xl font-bold text-gray-900">AI tools</h1>
            <Link to="/assessment-ai-playground" className="mt-2 inline-block text-primary-700 underline">Open assessment AI playground</Link>
            <p className="mt-2 text-sm text-gray-700">
              Choose an OpenAI model and system prompt per feature. Models are loaded from your
              OpenAI account (server-side API key).
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => modelsQuery.refetch()}
              disabled={modelsQuery.isFetching}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {modelsQuery.isFetching ? 'Refreshing…' : 'Refresh models'}
            </button>
            <button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save all'}
            </button>
          </div>
        </div>

        {showSuccessMessage && (
          <div className="mt-4 rounded-md bg-green-50 p-4">
            <p className="text-sm font-medium text-green-800">Settings saved.</p>
          </div>
        )}

        {saveMutation.isError && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">
              {axios.isAxiosError(saveMutation.error) && saveMutation.error.response?.data?.message
                ? String(saveMutation.error.response.data.message)
                : (saveMutation.error as Error)?.message || 'Save failed.'}
            </p>
          </div>
        )}

        {modelsUnavailable && (
          <div className="mt-4 rounded-md bg-amber-50 p-4 border border-amber-200">
            <p className="text-sm text-amber-900">
              OpenAI is not configured on the server (<code className="text-xs">OPENAI_API_KEY</code>
              ). Model list is unavailable; you can still type a model id manually and save.
            </p>
          </div>
        )}

        {modelsQuery.isError && !modelsUnavailable && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">Could not load model list from OpenAI.</p>
          </div>
        )}

        <div className="mt-8 space-y-8">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900">{feature.name}</h2>
              {feature.description ? (
                <p className="mt-1 text-sm text-gray-600">{feature.description}</p>
              ) : null}
              <p className="mt-1 text-xs text-gray-400 font-mono">id: {feature.id}</p>

              <div className="mt-4">
                <label
                  htmlFor={`model-${feature.id}`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Model
                </label>
                <input
                  id={`model-${feature.id}`}
                  type="text"
                  list={`model-datalist-${feature.id}`}
                  placeholder="e.g. gpt-4o-mini"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 font-mono text-sm"
                  value={form[feature.id]?.model ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      [feature.id]: {
                        ...prev[feature.id],
                        model: e.target.value,
                        prompt: prev[feature.id]?.prompt ?? '',
                      },
                    }))
                  }
                />
                <datalist id={`model-datalist-${feature.id}`}>
                  {modelOptions.map((id) => (
                    <option key={id} value={id} />
                  ))}
                </datalist>
                <p className="mt-1 text-xs text-gray-500">
                  Suggestions come from OpenAI; you can type any model id your key can access.
                </p>
              </div>

              <div className="mt-4">
                <label
                  htmlFor={`prompt-${feature.id}`}
                  className="block text-sm font-medium text-gray-700"
                >
                  Prompt
                </label>
                <textarea
                  id={`prompt-${feature.id}`}
                  rows={8}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 font-mono text-sm"
                  value={form[feature.id]?.prompt ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      [feature.id]: {
                        model: prev[feature.id]?.model ?? '',
                        prompt: e.target.value,
                      },
                    }))
                  }
                  placeholder="System or instruction prompt for this feature…"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default AiTools;
